// apps/frontend/src/components/AppUpdateNotice.tsx
// Banner Glowp qui s'affiche quand une nouvelle version de l'app est
// disponible (chunk lazy obsolète détecté par lazyWithRetry, ou nouveau
// service worker activé). L'user contrôle le moment du reload pour ne
// pas perdre une saisie en cours.

import React, { useEffect, useState, useCallback } from 'react';
import { Sparkles, RefreshCw, X } from 'lucide-react';
import { UPDATE_AVAILABLE_EVENT } from '../lib/lazyWithRetry';

const AppUpdateNotice: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [reloading, setReloading] = useState(false);

  useEffect(() => {
    const onUpdate = () => setVisible(true);
    window.addEventListener(UPDATE_AVAILABLE_EVENT, onUpdate);
    return () => window.removeEventListener(UPDATE_AVAILABLE_EVENT, onUpdate);
  }, []);

  const handleReload = useCallback(() => {
    setReloading(true);
    // Petit delay visuel pour que l'user voie le spinner avant le hard reload
    setTimeout(() => {
      window.location.reload();
    }, 250);
  }, []);

  const handleDismiss = useCallback(() => {
    setVisible(false);
    // Si l'user ferme, on lui re-propose dans 60s — la nouvelle version
    // est toujours dispo, on ne lâche pas l'affaire silencieusement.
    setTimeout(() => setVisible(true), 60_000);
  }, []);

  if (!visible) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[9999] flex justify-center px-4 pb-4 sm:pb-6 pointer-events-none"
      style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
      role="status"
      aria-live="polite"
    >
      <div className="pointer-events-auto w-full max-w-md rounded-2xl shadow-2xl ring-1 ring-black/5 overflow-hidden animate-[slideUp_0.3s_ease-out]">
        <div
          className="px-4 py-3.5 flex items-center gap-3"
          style={{
            background: 'linear-gradient(135deg, #C8A882 0%, #B89968 100%)',
          }}
        >
          <div className="flex-shrink-0 w-9 h-9 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-white" />
          </div>

          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-white">
              Nouvelle version dispo
            </div>
            <div className="text-xs text-white/85 mt-0.5 leading-snug">
              Glowp s'est mis à jour. Recharge pour continuer en toute fluidité.
            </div>
          </div>

          <button
            onClick={handleReload}
            disabled={reloading}
            className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full bg-white text-[#8B6914] hover:bg-white/95 disabled:opacity-70 transition-colors shadow-sm"
          >
            {reloading ? (
              <RefreshCw className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <RefreshCw className="w-3.5 h-3.5" />
            )}
            {reloading ? '...' : 'Recharger'}
          </button>

          <button
            onClick={handleDismiss}
            disabled={reloading}
            className="flex-shrink-0 p-1 text-white/70 hover:text-white transition-colors"
            aria-label="Fermer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default AppUpdateNotice;
